import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import { useGameContext } from '../../context/GameContext';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useSeason } from '../../context/SeasonContext';
import {
  getStandingsStaleMessage,
  mergeStandingsUsers,
  shouldShowStandingsStaleBanner,
} from '../../utils/standingsUtils';
import MobileStandingsCard from './MobileStandingsCard';
import styles from './MobileStandings.module.css';

const MobileStandings = ({ season, leaderboardData, onUserSelect }) => {
  const [allActiveUsers, setAllActiveUsers] = useState([]);
  const [expandedUsername, setExpandedUsername] = useState(null);
  const { user } = useAuth();
  const { settings } = useLeagueSettings();
  const { locksCache, getMaxPicksForWeek } = useGameContext();
  const { currentWeek } = useCurrentWeek();
  const { effectiveSeason } = useSeason();

  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        const response = await api.getActiveUsers();
        if (response.data && Array.isArray(response.data)) {
          setAllActiveUsers(response.data);
        }
      } catch (err) {
        console.error('Error fetching active users for standings:', err);
        setAllActiveUsers([]);
      }
    };

    fetchActiveUsers();
  }, []);

  const hasReachedMaxPicks = useMemo(() => {
    if (!currentWeek || !locksCache[currentWeek]) return false;

    const locksForWeek = locksCache[currentWeek];
    const userLocksCount = locksForWeek.filter(
      (lock) =>
        lock.userId === user?.id &&
        (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    return userLocksCount >= getMaxPicksForWeek(currentWeek);
  }, [currentWeek, locksCache, user?.id, getMaxPicksForWeek]);

  const users = useMemo(
    () =>
      mergeStandingsUsers(
        leaderboardData?.users,
        allActiveUsers,
        settings,
        leaderboardData?.weekStructure
      ),
    [leaderboardData, allActiveUsers, settings]
  );

  if (leaderboardData && !leaderboardData.seasonStarted) {
    return (
      <div className={styles.emptyState}>
        <h2>Standings</h2>
        <p>{leaderboardData.message || 'Season has not started yet.'}</p>
      </div>
    );
  }

  if (!users.length) {
    return (
      <div className={styles.emptyState}>
        <h2>Standings</h2>
        <p>No standings data available.</p>
      </div>
    );
  }

  const showStaleBanner = shouldShowStandingsStaleBanner({
    hasReachedMaxPicks,
    showLockers: settings.show_lockers,
    season,
    effectiveSeason,
  });

  return (
    <div className={styles.container}>
      {showStaleBanner && (
        <div className={styles.staleBanner}>{getStandingsStaleMessage(currentWeek)}</div>
      )}

      <p className={styles.hint}>Tap a card for details. Tap a name to view their locks.</p>

      <div className={styles.cardList}>
        {users.map((player) => (
          <MobileStandingsCard
            key={player.username}
            player={player}
            isCurrentUser={player.username === user?.username}
            isExpanded={expandedUsername === player.username}
            onToggleExpand={() =>
              setExpandedUsername((current) => (current === player.username ? null : player.username))
            }
            onUsernameClick={onUserSelect}
          />
        ))}
      </div>
    </div>
  );
};

MobileStandings.propTypes = {
  season: PropTypes.number.isRequired,
  leaderboardData: PropTypes.object,
  onUserSelect: PropTypes.func.isRequired,
};

export default MobileStandings;
